import {
  Button, Grid, Typography, Box, makeStyles,
  CircularProgress, FormControl, InputLabel, MenuItem, Select
} from '@material-ui/core'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import React, { useState, useEffect } from 'react'
import { getSiteSettings } from '../api/siteSettings'
import { useRecoilValue } from 'recoil'
import { userInfoState } from '../recoil/atoms'
import { thousandsSeparate, toFarsiDigits } from '../assets/utils'
import api from '../api/client'
import CardToCardDialog from '../components/billing/CardToCardDialog'

const AMOUNT_DEFAULT = 37500000   // ریال — واحد پول سامانه
const CARD_NUMBER_DEFAULT = '6037997129445590'
const PHONE_1_DEFAULT = '09166551132'
const PHONE_2_DEFAULT = '09166015508'

const useStyles = makeStyles((theme) => ({
  // دکمه پرداخت آنلاین
  onlinePayBtn: {
    background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    padding: theme.spacing(1.5, 4),
    borderRadius: 10,
    '&:hover': {
      background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
    },
    '&:disabled': {
      background: '#ccc',
      color: '#888',
    },
  },
  manualPayBtn: {
    borderColor: '#1a237e',
    color: '#1a237e',
    fontWeight: 'bold',
    fontSize: 14,
    padding: theme.spacing(1, 3),
    borderRadius: 10,
    '&:hover': {
      background: '#e8eaf6',
    },
  },
  // باکسِ راهِ جایگزین (کارت به کارت) — فقط بعد از شکستِ درگاه
  fallbackBox: {
    marginTop: 16,
    padding: theme.spacing(2),
    borderRadius: 12,
    background: '#fff8e1',
    border: '1px solid #ffe082',
  },
  fallbackTitle: {
    fontWeight: 'bold',
    color: '#8a6100',
    marginBottom: 6,
  },
  fallbackText: {
    color: '#5d4037',
    fontSize: 13.5,
    lineHeight: 2,
    marginBottom: 10,
  },
  errorBox: {
    background: '#ffebee',
    border: '1px solid #ef9a9a',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    textAlign: 'center',
    color: '#c62828',
    fontSize: 13,
  },
}))

const Payment = () => {
  const classes = useStyles()
  const [siteCfg, setSiteCfg] = useState(null)
  useEffect(() => {
    getSiteSettings().then((d) => d && setSiteCfg(d))
  }, [])
  // مقادیر از تنظیمات سایت؛ fallback: همان مقادیر هاردکد قبلی
  const AMOUNT = Number(siteCfg?.payment?.subscription_fee) || AMOUNT_DEFAULT
  const CARD_NUMBER = siteCfg?.payment?.card_number || CARD_NUMBER_DEFAULT
  const PHONE_1 = siteCfg?.contact?.phone2 || PHONE_1_DEFAULT
  const PHONE_2 = siteCfg?.contact?.phone || PHONE_2_DEFAULT
  const [open, setOpen] = useState(false)
  const [paying, setPaying] = useState(false)
  const [payError, setPayError] = useState('')
  const [years, setYears] = useState(1)
  const user = useRecoilValue(userInfoState)

  const handleOnlinePayment = async () => {
    setPaying(true)
    setPayError('')
    try {
      const response = await api.post('users/payment/sep/request/', { years })
      // حالت تست — بدون درگاه: پرداخت شبیه‌سازی و اشتراک فعال میشود
      if (response.data.test_mode) {
        setPaying(false)
        alert(response.data.message || 'پرداخت تستی با موفقیت ثبت شد.')
        window.location.reload()
        return
      }
      const { payment_url } = response.data
      // ریدایرکت به درگاه سامان
      window.location.href = payment_url
    } catch (err) {
      const msg = err?.response?.data?.error || 'خطا در اتصال به درگاه پرداخت. لطفاً دوباره تلاش کنید.'
      setPayError(msg)
      setPaying(false)
    }
  }

  return (
    <div>
      <Typography>
        با پرداخت حق عضویت (یکساله یا دوساله — با نرخ روز)، پیمانکاران محترم می توانند از قابلیت های زیر استفاده کنند:
        <ul>
          <li>دسترسی پیمانکاران به آخرین وضعیت قراردادهای منعقده با کلیه دستگاههای اجرایی</li>
          <li>دسترسی آنلاین 24 ساعته به اطلاعات قراردادها بدون نیاز به مراجعه حضوری</li>
          <li>تبادل دو طرفه پیام با دستگاههای اجرایی طرف قرارداد</li>
          <li>انتقال مدارک مورد نیاز دستگاههای اجرایی بدون نیاز به مراجعه حضوری</li>
        </ul>
      </Typography>

      <Grid container spacing={2} style={{ alignItems: 'center', justifyContent: 'center' }}>
        {/* انتخاب دورهٔ اشتراک: یکساله / دوساله (با نرخ امسال، بدون تخفیف) */}
        <Grid item>
          <FormControl size="small" variant="outlined" style={{ minWidth: 150 }}>
            <InputLabel>دورهٔ اشتراک</InputLabel>
            <Select value={years} onChange={(e) => setYears(Number(e.target.value))} label="دورهٔ اشتراک">
              <MenuItem value={1}>یکساله — {toFarsiDigits(thousandsSeparate(AMOUNT))} ریال</MenuItem>
              <MenuItem value={2}>دوساله — {toFarsiDigits(thousandsSeparate(AMOUNT * 2))} ریال</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item style={{ textAlign: 'center' }}>
          <Typography style={{ color: 'red' }}>
            {toFarsiDigits(thousandsSeparate(AMOUNT * years))} ریال
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            className={classes.onlinePayBtn}
            startIcon={paying ? <CircularProgress size={18} color="inherit" /> : <LockOpenIcon />}
            onClick={handleOnlinePayment}
            disabled={paying}
          >
            {paying ? 'در حال اتصال به درگاه...' : 'پرداخت آنلاین'}
          </Button>
        </Grid>
      </Grid>

      {payError && (
        <Box className={classes.errorBox}>
          {payError}
        </Box>
      )}

      {/* راهِ جایگزین — فقط وقتی پرداختِ آنلاین از درگاه شکست خورده */}
      {payError && (
        <Box className={classes.fallbackBox}>
          <Typography className={classes.fallbackTitle}>
            پرداخت آنلاین انجام نشد — راهِ جایگزین: کارت به کارت
          </Typography>
          <Typography className={classes.fallbackText}>
            اگر اتصال به درگاه بانکی برای شما ممکن نیست، می‌توانید مبلغِ اشتراک را
            {' '}{years === 2 ? 'دو‌ساله' : 'یک‌ساله'} ({toFarsiDigits(thousandsSeparate(AMOUNT * years))} ریال)
            {' '}به شمارهٔ کارت سامانه واریز کنید. پس از واریز، شناسهٔ ملی و نام شرکت خود را
            به یکی از شماره‌های پشتیبانی پیامک کنید تا اشتراک شما فعال شود.
          </Typography>
          <Button
            variant="outlined"
            className={classes.manualPayBtn}
            onClick={() => setOpen(true)}
          >
            مشاهدهٔ شماره کارت و مراحل پرداخت
          </Button>
        </Box>
      )}

      {/* دیالوگ کارت به کارت — فقط بعد از شکستِ درگاه */}
      <CardToCardDialog
        open={open}
        onClose={() => setOpen(false)}
        cardNumber={CARD_NUMBER}
        phones={[PHONE_1, PHONE_2]}
        amount={AMOUNT * years}
        years={years}
      />
    </div>
  )
}

export default Payment
